import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '../axios-instance';
import { GET_URL_NEW } from '@utils/api/endpoints';
import { BaseError } from '@utils/types/shared/base';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params';

export interface BillingCycle {
  id: number;
  cycle_start: string; // ISO date
  cycle_end: string; // ISO date
  status: string;
  is_locked: boolean;
  locked_at: string | null;
}

export interface BillingCycleResponse {
  total_results: number;
  result: BillingCycle[];
}

export const useGetBillingCycles = () => {
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const sortField = (searchParams.sort as string | undefined) || undefined;
  const sortDirection =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;

  const qString = (searchParams.q as string | undefined) || undefined;

  // same default: 1st day of current month to today
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const cycleStart = startOfMonth.toISOString().split('T')[0];
  const cycleEnd = now.toISOString().split('T')[0];

  const fetchCycles = async () => {
    const { billingCyclesWith } = GET_URL_NEW({
      offset,
      limit,
      q: qString,
      sort: sortField,
      direction: sortDirection
    });

    // inject cycle range
    const url = billingCyclesWith({
      cycle_start: cycleStart,
      cycle_end: cycleEnd
    });

    const { data } = await Axios.get<BillingCycleResponse>(url);
    return data;
  };

  return useQuery<BillingCycleResponse, AxiosError<BaseError>>(
    [
      'billingCycles',
      offset,
      limit,
      qString,
      sortField,
      sortDirection,
      cycleStart,
      cycleEnd
    ],
    fetchCycles,
    {
      useErrorBoundary: false,
      keepPreviousData: true
    }
  );
};
